import { Avatar, AvatarProps } from '@mui/material';
import { BaseReactComponent, Color } from '../../../types';

export type SizeType = 'xsmall' | 'small' | 'medium' | 'large' | 'xlarge';

const SIZES: Record<SizeType, { size: number; fontSize: number }> = {
  xsmall: { size: 20, fontSize: 11 },
  small: { size: 24, fontSize: 12 },
  medium: { size: 32, fontSize: 16 },
  large: { size: 40, fontSize: 20 },
  xlarge: { size: 70, fontSize: 32 }
};

export interface HiAvatarProps extends BaseReactComponent {
  /**
   * Used to render icon or text elements inside the Avatar. This can be an element, or just a string.
   */
  children?: React.ReactNode;
  /**
   * If internal user, display Avatar colors reversed.
   * @default false
   */
  internal?: boolean;
  /**
   * The Avatar main color.
   */
  color?: Color;
  /**
   * The Avatar size.
   * @default medium
   */
  size?: SizeType;
  /**
   * Image to display as content.
   */
  image?: string;
  /**
   * Add a shadow around the Avatar. Use it only for white background images.
   * @default false
   */
  shadow?: boolean;
  /**
   * Override Mui Avatar props
   * (https://mui.com/material-ui/api/avatar/).
   */
  AvatarProps?: AvatarProps;
}

/*
 * The Avatar component is a visual representation of a user or entity. It either shows an image or a text (usually initials or numbers).
 *
 * It extends Mui's Avatar.
 */
const HiAvatar = ({
  internal = false,
  color,
  size = 'medium',
  image,
  shadow = false,
  AvatarProps,
  ...other
}: HiAvatarProps) => {
  return (
    <Avatar
      {...other}
      {...AvatarProps}
      src={image}
      sx={{
        bgcolor: internal ? 'white' : color,
        border: internal ? `solid 1px ${color}` : 'none',
        boxSizing: internal ? 'border-box' : 'inherit',
        color: internal ? color : 'auto',
        width: SIZES[size].size,
        height: SIZES[size].size,
        fontSize: SIZES[size].fontSize,
        boxShadow: shadow ? 3 : 'none'
      }}
    />
  );
};

export default HiAvatar;
